import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  User,
  AuthTokens,
  LoginFormData,
  RegisterFormData,
  APIKeyLoginFormData,
  ForgotPasswordFormData,
  ResetPasswordFormData,
  ChangePasswordFormData,
  UpdateProfileFormData,
} from '@/types'
import { authService } from '@/services/authService'
import toast from 'react-hot-toast'

interface AuthState {
  user: User | null
  tokens: AuthTokens | null
  isLoading: boolean
  isAuthenticated: boolean
  authMethod: 'email' | 'api_key' | null
}

interface AuthActions {
  // Standard Authentication
  login: (data: LoginFormData) => Promise<void>
  register: (data: RegisterFormData) => Promise<void>
  logout: () => Promise<void>

  // API Key Authentication
  loginWithAPIKey: (data: APIKeyLoginFormData) => Promise<void>
  logoutAPIKey: () => Promise<void>

  // Email Verification
  verifyEmail: (token: string) => Promise<void>
  resendVerificationEmail: () => Promise<void>

  // Password Management
  forgotPassword: (data: ForgotPasswordFormData) => Promise<void>
  resetPassword: (data: ResetPasswordFormData) => Promise<void>
  changePassword: (data: ChangePasswordFormData) => Promise<void>

  // Profile Management
  updateProfile: (data: UpdateProfileFormData) => Promise<void>
  switchEnvironment: (environment: 'production' | 'sandbox') => Promise<void>

  // Admin User Management
  getAdminUsers: (params?: {
    search?: string
    page?: number
    page_size?: number
  }) => Promise<{ results: any[]; count: number }>
  searchAdminUsers: (query: string) => Promise<any[]>

  // Utility Methods
  checkAuth: () => Promise<void>
  updateUser: (user: User) => void
  clearAuth: () => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // State
      user: null,
      tokens: null,
      isLoading: false,
      isAuthenticated: false,
      authMethod: null,

      // Standard Authentication
      login: async (data: LoginFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.login(data)
          set({
            user: response.user,
            tokens: response.tokens,
            isAuthenticated: true,
            authMethod: 'email',
            isLoading: false,
          })
          toast.success(response.message || 'Login successful!')
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      register: async (data: RegisterFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.register(data)
          set({
            user: response.user,
            tokens: response.tokens,
            isAuthenticated: true,
            authMethod: 'email',
            isLoading: false,
          })
          toast.success(response.message || 'Registration successful!')
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      logout: async () => {
        set({ isLoading: true })
        try {
          const { authMethod } = get()
          if (authMethod === 'api_key') {
            await authService.logoutAPIKey()
          } else {
            await authService.logout()
          }
        } catch (error) {
          console.warn('Logout API call failed:', error)
        } finally {
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            authMethod: null,
            isLoading: false,
          })
          toast.success('Logged out successfully')
        }
      },

      // API Key Authentication
      loginWithAPIKey: async (data: APIKeyLoginFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.loginWithAPIKey(data)
          set({
            user: response.user,
            tokens: response.tokens,
            isAuthenticated: true,
            authMethod: 'api_key',
            isLoading: false,
          })
          toast.success(response.message || 'API key authentication successful!')
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      logoutAPIKey: async () => {
        await get().logout()
      },

      // Email Verification
      verifyEmail: async (token: string) => {
        set({ isLoading: true })
        try {
          const response = await authService.verifyEmail(token)
          toast.success(response.message || 'Email verified successfully!')
        } catch (error: any) {
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      resendVerificationEmail: async () => {
        set({ isLoading: true })
        try {
          const response = await authService.resendVerificationEmail()
          toast.success(response.message || 'Verification email sent!')
        } catch (error: any) {
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Password Management
      forgotPassword: async (data: ForgotPasswordFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.forgotPassword(data)
          toast.success(response.message || 'Password reset email sent!')
        } catch (error: any) {
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      resetPassword: async (data: ResetPasswordFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.resetPassword(data)
          toast.success(response.message || 'Password reset successfully!')
        } catch (error: any) {
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      changePassword: async (data: ChangePasswordFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.changePassword(data)
          toast.success(response.message || 'Password changed successfully!')
        } catch (error: any) {
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Profile Management
      updateProfile: async (data: UpdateProfileFormData) => {
        set({ isLoading: true })
        try {
          const response = await authService.updateProfile(data)
          set({
            user: response.user,
            isLoading: false,
          })
          toast.success(response.message || 'Profile updated successfully!')
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      switchEnvironment: async (environment: 'production' | 'sandbox') => {
        const currentUser = get().user
        if (!currentUser || currentUser.environment === environment) {
          return
        }

        set({ isLoading: true })
        try {
          const response = await authService.updateProfile({ environment })
          set({
            user: response.user,
            isLoading: false,
          })
          const readableEnvironment = environment === 'production' ? 'Production' : 'Sandbox'
          toast.success(`Switched to ${readableEnvironment} environment`)
        } catch (error: any) {
          set({ isLoading: false })
          const errorMessage = authService.handleAuthError(error)
          toast.error(errorMessage)
          throw error
        }
      },

      // Admin User Management
      getAdminUsers: async (params = {}) => {
        set({ isLoading: true })
        try {
          const { tokens } = get()
          if (!tokens?.access) {
            throw new Error('No access token available')
          }

          const searchParams = new URLSearchParams()
          if (params.search) searchParams.append('search', params.search)
          if (params.page) searchParams.append('page', params.page.toString())
          if (params.page_size) searchParams.append('page_size', params.page_size.toString())

          const response = await fetch(`/api/auth/admin/users/?${searchParams.toString()}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokens.access}`,
            },
          })

          if (!response.ok) {
            throw new Error(`Failed to fetch users: ${response.statusText}`)
          }

          const data = await response.json()
          if (!data.success) {
            throw new Error(data.message || 'Failed to fetch users')
          }

          return {
            results: data.data.results,
            count: data.data.count,
          }
        } catch (error: any) {
          console.error('Failed to fetch admin users:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      searchAdminUsers: async (query: string) => {
        set({ isLoading: true })
        try {
          const { tokens } = get()
          if (!tokens?.access) {
            throw new Error('No access token available')
          }

          const response = await fetch(
            `/api/auth/admin/users/?search=${encodeURIComponent(query)}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokens.access}`,
              },
            }
          )

          if (!response.ok) {
            throw new Error(`Failed to search users: ${response.statusText}`)
          }

          const data = await response.json()
          if (!data.success) {
            throw new Error(data.message || 'Failed to search users')
          }

          return data.data.results
        } catch (error: any) {
          console.error('Failed to search admin users:', error)
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      // Utility Methods
      checkAuth: async () => {
        const { tokens } = get()
        if (!tokens?.access) {
          set({ isAuthenticated: false })
          return
        }

        try {
          const response = await authService.getProfile()
          set({
            user: response.user,
            isAuthenticated: true,
          })
        } catch (error) {
          set({
            user: null,
            tokens: null,
            isAuthenticated: false,
            authMethod: null,
          })
        }
      },

      updateUser: (user: User) => {
        set({ user })
      },

      clearAuth: () => {
        set({
          user: null,
          tokens: null,
          isAuthenticated: false,
          authMethod: null,
        })
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        tokens: state.tokens,
        isAuthenticated: state.isAuthenticated,
        authMethod: state.authMethod,
      }),
    }
  )
)
